export const LLM_PROVIDERS = Object.freeze({
  OLLAMA: "ollama",
  OPENAI: "openai",
  GEMINI: "gemini",
});

export const LLM_SENSITIVITY = Object.freeze({
  PUBLIC: "public",
  INTERNAL: "internal",
  PRIVATE_VAULT: "private_vault",
  STUDENT_PII: "student_pii",
  CONFIDENTIAL: "confidential",
});

export const LLM_POLICY_VERSION = "2026-02-27";

const LOCAL_ONLY_SENSITIVITY = new Set([
  LLM_SENSITIVITY.PRIVATE_VAULT,
  LLM_SENSITIVITY.STUDENT_PII,
  LLM_SENSITIVITY.CONFIDENTIAL,
]);

const CLOUD_PROVIDERS = [LLM_PROVIDERS.OPENAI, LLM_PROVIDERS.GEMINI];

const normalizeStringValue = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export const normalizeProvider = (value) => {
  const normalized = normalizeStringValue(value);
  if (normalized === LLM_PROVIDERS.OLLAMA) return LLM_PROVIDERS.OLLAMA;
  if (normalized === LLM_PROVIDERS.OPENAI) return LLM_PROVIDERS.OPENAI;
  if (normalized === LLM_PROVIDERS.GEMINI) return LLM_PROVIDERS.GEMINI;
  return null;
};

export const normalizeSensitivity = (value) => {
  const normalized = normalizeStringValue(value);
  if (normalized === LLM_SENSITIVITY.PUBLIC) return LLM_SENSITIVITY.PUBLIC;
  if (normalized === LLM_SENSITIVITY.INTERNAL) return LLM_SENSITIVITY.INTERNAL;
  if (normalized === LLM_SENSITIVITY.PRIVATE_VAULT) {
    return LLM_SENSITIVITY.PRIVATE_VAULT;
  }
  if (normalized === LLM_SENSITIVITY.STUDENT_PII) {
    return LLM_SENSITIVITY.STUDENT_PII;
  }
  if (normalized === LLM_SENSITIVITY.CONFIDENTIAL) {
    return LLM_SENSITIVITY.CONFIDENTIAL;
  }
  return null;
};

const normalizeContextTags = (rawTags) => {
  if (!Array.isArray(rawTags)) return [];
  return rawTags
    .map((tag) => normalizeStringValue(tag))
    .filter(Boolean)
    .slice(0, 24);
};

export const inferSensitivityFromMetadata = (metadata = {}) => {
  const explicitSensitivity = normalizeSensitivity(
    metadata.llmSensitivity ||
      metadata.dataClassification ||
      metadata.sensitivity
  );
  if (explicitSensitivity) {
    return explicitSensitivity;
  }

  const tags = normalizeContextTags(
    metadata.llmContextTags || metadata.contextTags || metadata.tags
  );
  if (tags.includes("private_vault")) {
    return LLM_SENSITIVITY.PRIVATE_VAULT;
  }
  if (tags.includes("student_pii") || tags.includes("gradebook")) {
    return LLM_SENSITIVITY.STUDENT_PII;
  }

  if (metadata.privateVault === true) {
    return LLM_SENSITIVITY.PRIVATE_VAULT;
  }
  if (metadata.containsStudentPii === true || metadata.gradebookData === true) {
    return LLM_SENSITIVITY.STUDENT_PII;
  }

  return LLM_SENSITIVITY.INTERNAL;
};

export const isCloudProvider = (provider) =>
  CLOUD_PROVIDERS.includes(normalizeProvider(provider));

const getDefaultCloudProvider = () =>
  normalizeProvider(process.env.REACT_APP_DEFAULT_AI_PROVIDER) ||
  LLM_PROVIDERS.GEMINI;

const getAllowedProvidersForSensitivity = (sensitivity) =>
  LOCAL_ONLY_SENSITIVITY.has(sensitivity)
    ? [LLM_PROVIDERS.OLLAMA]
    : [...CLOUD_PROVIDERS];

export const resolveLlmRoute = ({ requestedProvider = null, metadata = {} } = {}) => {
  const sensitivity = inferSensitivityFromMetadata(metadata);
  const requested = normalizeProvider(requestedProvider);
  const allowedProviders = getAllowedProvidersForSensitivity(sensitivity);
  const fallbackProvider =
    sensitivity === LLM_SENSITIVITY.PUBLIC || sensitivity === LLM_SENSITIVITY.INTERNAL
      ? getDefaultCloudProvider()
      : LLM_PROVIDERS.OLLAMA;

  const provider = requested && allowedProviders.includes(requested)
    ? requested
    : allowedProviders.includes(fallbackProvider)
      ? fallbackProvider
      : allowedProviders[0];

  return {
    provider,
    requestedProvider: requested,
    sensitivity,
    allowedProviders,
    requiresLocal: LOCAL_ONLY_SENSITIVITY.has(sensitivity),
    allowFallback: !LOCAL_ONLY_SENSITIVITY.has(sensitivity),
    blockedRequestedProvider: Boolean(
      requested && !allowedProviders.includes(requested)
    ),
    policyVersion: LLM_POLICY_VERSION,
  };
};
